<template>
  <div class="table-settings">
    <slot name="before-slot" />
    <div
      v-if="showVerticalSeparator && $slots['before-slot']"
      class="table-settings__separator before-separator"
    />

    <MLink
      :disabled="disabled"
      class="table-settings__settings"
      :icon="iconGears"
      @click="editPreset(null)"
    >
      <div
        v-if="showLabels"
      >
        {{ customLabel ? customLabel : _getTr("table_settings") }}
      </div>
    </MLink>
    <DropdownMenu
      v-if="withPresets"
      ref="presetsDropdown"
      :disabled="disabled"
      :icon="iconFolder"
      :fix-width="true"
      :title="_getTr('column_presets')"
      class="table-settings__dropdown"
    >
      <TableSettingsPresets
        :grouped-presets="groupedPresets"
        :active-preset-id="activePresetId"
        @selectPreset="handleSelectPreset"
        @editPreset="handleEditPreset"
        @deletePreset="handleDeletePreset"
      />
    </DropdownMenu>

    <div
      v-if="showVerticalSeparator && $slots.default"
      class="table-settings__separator"
    />
    <slot />
  </div>
</template>

<script>
  import MLink from 'components/Links/MLink';
  import iconGears from 'assets/icons/gear.svg';
  import iconFolder from 'assets/icons/folder.svg';
  import DropdownMenu from 'components/Dropdowns/DropdownMenu';
  import TableSettingsPresets from 'components/Data/TableSettings/TableSettingsPresets/TableSettingsPresets';
  import MModalMixin from 'components/Modals/MModal/MModalMixin';
  import TableSettingsModal from 'components/Data/TableSettings/TableSettingsModal/TableSettingsModal';
  import _ from 'lodash';
  _.noConflict();

  export default {
    name: 'TableSettings',
    components: {TableSettingsPresets, MLink, DropdownMenu},
    mixins: [MModalMixin],
    props: {
      value: {
        type: Array,
        required: true
      },
      disabled: {
        type: Boolean,
        default: false
      },
      availableColumns: {
        type: Array,
        required: true
      },
      defaultEnabledPreset: {
        type: Array,
        default: Array
      },
      customLabel: {
        type: String,
        default: ''
      },
      withPresets: {
        type: Boolean,
        default: false
      },
      showVerticalSeparator: {
        type: Boolean,
        default: false
      },
      showLabels: {
        type: Boolean,
        default: true
      },
      groupedPresets: {
        type: Array,
        default: Array
      },
      activePresetId: {
        type: Number,
        default: null
      },
      preserveSorting: {
        type: Boolean,
        default: false
      },
      canEditPresetPredicate: {
        type: Function,
        default: () => true
      },
      canDeletePresetPredicate: {
        type: Function,
        default: () => true
      },
      presetSaveFormComponent: {
        type: Object,
        default: null
      },
      presetSaveFormPayload: {
        type: Object,
        default: null
      },
      presetSaveFormHandlers: {
        type: Object,
        default: Object
      }
    },
    data () {
      return {
        iconGears,
        iconFolder,

        modalComponent: TableSettingsModal,
        modalProps: {
          isEditing: false,
          availableColumns: []
        },
        modalEventListeners: {
          applyPreset: this.applyPreset,
          cancel: this.cancelEditPreset,
          saveAndApplyPreset: this.saveAndApplyPreset
        }
      };
    },
    computed: {
      columnPresetsMap () {
        const flat = this.groupedPresets.map(g => g.items).flat();

        return _.keyBy(flat, 'id');
      },
      columnsFlat () {
        return this.availableColumns.map(g => g.children.map(c => {
          const groupInfo = {
            id: g.id,
            name: g.name,
            union: g.nonBreakable,
            icon: g.icon
          };

          if (c.children && c.children.length > 0) {
            return c.children.map(i => ({
              groupInfo,
              ...i
            }));
          }
          return {
            groupInfo,
            ...c
          };
        })).flat(2);
      },
      availableColumnIdsSet () {
        return new Set(this.columnsFlat.map(c => c.id));
      }
    },
    methods: {
      editPreset (presetIdOrNone) {
        if (this.disabled) {
          return;
        }
        this.modalProps.preserveSorting = this.preserveSorting;
        this.modalProps.isEditing = false;
        this.modalProps.presetSaveFormComponent = this.presetSaveFormComponent;
        this.modalProps.presetSaveFormPayload = this.presetSaveFormPayload;
        this.modalProps.presetSaveFormHandlers = this.presetSaveFormHandlers;
        this.modalProps.availableColumns = this.availableColumns;
        this.modalProps.defaultColumns = this.defaultEnabledPreset;
        this.modalProps.availableColumnsFlat = this.columnsFlat;

        if (presetIdOrNone) {
          const preset = this.columnPresetsMap[presetIdOrNone];
          const presetColumns = preset.columns.filter(c => this.availableColumnIdsSet.has(c));

          this.modalProps.isEditing = true;
          this.modalProps.value = presetColumns;
          this.modalProps.presetId = presetIdOrNone;
          this.modalProps.presetData = preset;
        } else {
          this.modalProps.value = this.value;
          this.modalProps.presetId = null;
          this.modalProps.presetData = null;
        }

        this.showPopup();
      },
      applyPreset ({columns}) {
        this.$emit('input', columns);
        this.hidePopup();
      },
      cancelEditPreset () {
        this.hidePopup();
      },
      saveAndApplyPreset (presetId) {
        this.handleSelectPreset(presetId);
        this.hidePopup();
      },
      handleSelectPreset (presetId) {
        const preset = this.columnPresetsMap[presetId];
        const presetColumns = preset.columns.filter(c => this.availableColumnIdsSet.has(c));

        this.$emit('input', presetColumns);
        this.$emit('activatePreset', presetId);

        this.$refs.presetsDropdown.closeDropdown();
      },
      handleEditPreset (presetId) {
        this.editPreset(presetId);
      },
      handleDeletePreset (presetId) {
        this.$emit('deletePreset', presetId);
        this.$refs.presetsDropdown.closeDropdown();
      }
    }
  };
</script>

<style>
  .table-settings {
    font-size: 14px;
    font-weight: var(--medium-font-weight);
  }

  .table-settings__settings .m-link__icon {
    width: 16px;
    height: 16px;
  }

  .table-settings,
  .table-settings__dropdown .popper__wrapper {
    display: flex;
    align-items: center;
  }

  .table-settings__dropdown {
    margin-left: 12px;
  }

  .table-settings__dropdown .popper__wrapper span {
    display: block;
    height: 18px;
  }

  .table-settings__separator {
    width: 1px;
    height: 16px;

    margin-left: 12px;

    border-left: solid 1px var(--border-color);

    &.before-separator {
      margin-right: 12px;
    }
  }
</style>
